import React from 'react';
import { cn, formatTimestamp } from '@/lib/utils';
import {
  Bell,
  CheckCircle2,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  ShieldAlert,
  Activity,
  Zap,
  XCircle,
  HelpCircle,
} from 'lucide-react';

export interface LiveEvent {
  id: string;
  type: string;
  timestamp: string | number;
  message: string;
  symbol?: string;
  severity?: 'info' | 'success' | 'warning' | 'error';
  data?: Record<string, unknown>;
}

interface LiveEventItemProps {
  event: LiveEvent;
  className?: string;
}

export const LiveEventItem: React.FC<LiveEventItemProps> = ({ event, className }) => {
  const getIcon = () => {
    switch (event.type.toLowerCase()) {
      case 'trade_executed':
      case 'position_opened':
        return <ArrowUpRight className="w-3.5 h-3.5 text-emerald-400" />;
      case 'position_closed':
        return <ArrowDownRight className="w-3.5 h-3.5 text-rose-400" />;
      case 'signal_accepted':
      case 'signal_received':
        return <Zap className="w-3.5 h-3.5 text-cyan-400" />;
      case 'signal_blocked':
        return <ShieldAlert className="w-3.5 h-3.5 text-amber-400" />;
      case 'signal_rejected':
        return <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />;
      case 'order_rejected':
      case 'order_failed':
        return <XCircle className="w-3.5 h-3.5 text-rose-400" />;
      case 'execution_unknown':
        return <HelpCircle className="w-3.5 h-3.5 text-purple-400" />;
      case 'risk_alert':
      case 'risk_halt':
      case 'square_off_triggered':
        return <ShieldAlert className="w-3.5 h-3.5 text-rose-400" />;
      case 'cycle_completed':
        return <Activity className="w-3.5 h-3.5 text-slate-400" />;
      default:
        return event.severity === 'error' ? (
          <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />
        ) : event.severity === 'warning' ? (
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
        ) : event.severity === 'success' ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
        ) : (
          <Bell className="w-3.5 h-3.5 text-cyan-400" />
        );
    }
  };

  const getBorderColor = () => {
    if (event.severity === 'error') return 'border-rose-900/60 bg-rose-950/10';
    if (event.severity === 'warning') return 'border-amber-900/60 bg-amber-950/10';
    if (event.severity === 'success') return 'border-emerald-900/60 bg-emerald-950/10';
    return 'border-slate-800/80 bg-slate-900/40';
  };

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-3 rounded-lg border text-xs transition-all duration-200 ease-out hover:bg-slate-800/60 animate-fade-in-up interactive-card',
        getBorderColor(),
        className
      )}
    >
      <div className="p-1.5 rounded-md bg-slate-900 border border-slate-800 shrink-0 mt-0.5 shadow-sm">
        {getIcon()}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <div className="flex items-center gap-1.5">
            <span className="font-mono font-semibold uppercase tracking-wider text-[10px] text-slate-400">
              {event.type.replace(/_/g, ' ')}
            </span>
            {event.symbol && (
              <span className="px-1.5 py-0.2 rounded bg-slate-800 text-[10px] font-mono font-bold text-emerald-300">
                {event.symbol}
              </span>
            )}
          </div>
          <span className="text-[10px] font-mono text-slate-500 shrink-0 tabular-nums">
            {formatTimestamp(event.timestamp, 'time')}
          </span>
        </div>
        <p className="text-slate-300 text-xs font-sans break-words">{event.message}</p>
      </div>
    </div>
  );
};
